import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CredentialStatusSchema,
  SetupLinkResponseSchema,
  type SetupLinkResponse,
  EmployeeSchema,
  RoleAssignmentSummarySchema,
  RoleSummarySchema,
  UserSummarySchema,
  type EmployeeDto,
  type ClientDto,
  type ProjectDto,
  type RoleAssignmentSummaryDto,
  type RoleSummaryDto,
  type UserSummaryDto,
} from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { z } from 'zod';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { getData, getJson, patchJson, postJson } from '../../shared/api/client';
import { postAuth } from '../auth/auth-api';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonAvatar, PersonName } from '../../shared/ui/person';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { EmployeePicker } from '../finance/lookups';
import { businessToday } from '../finance/line-editor';
import { ActiveClientPicker, ActiveProjectPicker } from '../crm/common';
import { useScopeNames } from './scope';
import { ScopeText } from './ScopeText';
import { assignmentsKey, useAssignments, USERS } from './UsersPage';

/**
 * One user (`/admin/users/:id`; SDD §11.3, §11.5; FRD-USR-002, USR-005, USR-008): who they are,
 * the employee record they are, and their role assignments — current, scheduled and ended — with
 * the actions an administrator may take: edit the name, activate or deactivate, link or unlink the
 * employee, assign a role with an optional organizational scope, and end an assignment. Every
 * action is a privileged, audited action the API checks on each request.
 */
export function UserDetailPage() {
  const { id = '' } = useParams();
  const user = useQuery({
    queryKey: ['platform', 'users', id],
    queryFn: () => getData(`${USERS}/${id}`, UserSummarySchema),
  });
  const back = (
    <Button component={RouterLink} to="/admin/users" startIcon={<ArrowBackIcon />}>
      All users
    </Button>
  );
  if (user.isPending) return <LoadingState label="Loading the user" />;
  if (user.isError) {
    return <RecordFailure title="User" noun="user" error={user.error} back={back} />;
  }
  return <UserRecord user={user.data} back={back} />;
}

function UserRecord({ user, back }: { user: UserSummaryDto; back: ReactNode }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canChange = hasCapability(session.data, 'platform.app_user:change');
  const canSeeAssignments = hasCapability(session.data, 'platform.role_assignment:view');
  const canAssign = hasCapability(session.data, 'platform.role_assignment:create');
  const [renaming, setRenaming] = useState(false);
  const status = useMutation({
    mutationFn: () =>
      patchJson(
        `${USERS}/${user.id}`,
        { status: user.status === 'active' ? 'inactive' : 'active' },
        UserSummarySchema,
        session.data?.csrfToken ?? '',
        user.version,
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });
    },
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title={user.displayName}
        description={user.email}
        leading={
          <PersonAvatar
            name={user.displayName}
            employeeId={user.employeeId}
            userId={user.id}
            size={40}
          />
        }
        actions={
          <>
            {back}
            {canChange && (
              <Button
                variant="outlined"
                color={user.status === 'active' ? 'warning' : 'primary'}
                disabled={status.isPending}
                onClick={() => {
                  status.mutate();
                }}
              >
                {user.status === 'active' ? 'Deactivate user' : 'Activate user'}
              </Button>
            )}
            {canChange && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setRenaming(true);
                }}
              >
                Edit name
              </Button>
            )}
          </>
        }
      />
      {status.isError && <ErrorState message={status.error.message} />}
      <Section title="User">
        <Facts
          label="User details"
          facts={[
            [
              'Name',
              <PersonName
                key="n"
                name={user.displayName}
                employeeId={user.employeeId}
                userId={user.id}
              />,
            ],
            ['Email (sign-in)', user.email],
            ['Status', <StatusChip key="s" status={user.status} />],
            ['Employee record', <LinkedEmployee key="e" employeeId={user.employeeId} />],
          ]}
        />
        {user.status === 'inactive' && (
          <Alert severity="info" variant="outlined" sx={{ m: 2 }}>
            An inactive user cannot sign in and holds no access, whatever roles are assigned.
          </Alert>
        )}
      </Section>
      <SignInSection user={user} canChange={canChange} />
      {canChange && <EmployeeLinkSection key={`${user.id}:${String(user.version)}`} user={user} />}
      {canSeeAssignments && <Assignments user={user} />}
      {canAssign && <AssignRole user={user} />}
      <RecordHistory recordType="platform.app_user" recordId={user.id} />
      {renaming && (
        <RenameDialog
          key={String(user.version)}
          user={user}
          onClose={() => {
            setRenaming(false);
          }}
        />
      )}
    </Stack>
  );
}

/**
 * Built-in sign-in (SDD §11.1.2; FRD-USR-025, USR-029): the credential's state — never a secret —
 * and the one-time setup link. For a user who already has a credential, issuing a link is a reset:
 * it revokes the password, authenticator and recovery codes and signs the user out everywhere.
 */
function SignInSection({ user, canChange }: { user: UserSummaryDto; canChange: boolean }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [issued, setIssued] = useState<SetupLinkResponse | null>(null);
  const status = useQuery({
    queryKey: ['platform', 'users', user.id, 'credential'],
    retry: false,
    queryFn: () => getJson(`${USERS}/${user.id}/credential`, CredentialStatusSchema),
  });
  const issue = useMutation({
    mutationFn: () =>
      postAuth(
        `${USERS}/${user.id}/setup-link`,
        {},
        SetupLinkResponseSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: (link) => {
      setConfirming(false);
      if (link !== undefined) setIssued(link);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'users', user.id] });
    },
  });
  const state = status.data?.state;
  const hasCredential = state === 'active' || state === 'locked';
  const label = hasCredential ? 'Reset sign-in' : 'Issue setup link';
  return (
    <Section
      title="Sign-in"
      actions={
        canChange &&
        user.status === 'active' && (
          <Button
            variant="outlined"
            color={hasCredential ? 'warning' : 'primary'}
            disabled={status.isPending}
            onClick={(event) => {
              event.currentTarget.focus();
              setConfirming(true);
            }}
          >
            {label}
          </Button>
        )
      }
    >
      {status.isPending && <LoadingState label="Loading the sign-in state" />}
      {status.isError && <ErrorState message={status.error.message} />}
      {status.data !== undefined && (
        <Facts
          label="Sign-in"
          facts={[
            ['Built-in sign-in', CREDENTIAL_STATES[status.data.state]],
            ...(status.data.lockedUntil !== null
              ? [['Locked until', formatDateTime(status.data.lockedUntil)] as [string, string]]
              : []),
            ...(status.data.passwordChangedAt !== null
              ? [
                  ['Password set', formatDateTime(status.data.passwordChangedAt)] as [
                    string,
                    string,
                  ],
                ]
              : []),
            ...(hasCredential
              ? [
                  [
                    'Authenticator app',
                    status.data.authenticatorEnrolled ? 'Added' : 'Not added (password only)',
                  ] as [string, string],
                ]
              : []),
            ...(hasCredential && status.data.authenticatorEnrolled
              ? [
                  ['Unused recovery codes', String(status.data.recoveryCodesRemaining)] as [
                    string,
                    string,
                  ],
                ]
              : []),
            [
              'Open setup link',
              status.data.setupLinkExpiresAt === null
                ? 'None'
                : `Valid until ${formatDateTime(status.data.setupLinkExpiresAt)}`,
            ],
          ]}
        />
      )}
      {confirming && (
        <FormDialog
          open
          title={label}
          submitLabel={label}
          canSubmit
          submitting={issue.isPending}
          error={issue.isError ? issue.error.message : null}
          onClose={() => {
            setConfirming(false);
          }}
          onSubmit={() => {
            issue.mutate();
          }}
        >
          <Typography>
            {hasCredential
              ? `This revokes ${user.displayName}'s password, and their authenticator and recovery codes if they have them, signs them out everywhere, and creates a new one-time setup link.`
              : `This creates a one-time setup link for ${user.displayName}. They choose their own password through it, and add an authenticator app where one is required.`}{' '}
            Any earlier link stops working.
          </Typography>
        </FormDialog>
      )}
      {issued !== null && (
        <SetupLinkDialog
          link={issued}
          name={user.displayName}
          onClose={() => {
            setIssued(null);
          }}
        />
      )}
    </Section>
  );
}

const CREDENTIAL_STATES: Readonly<Record<string, string>> = {
  none: 'Not set up',
  active: 'Active',
  locked: 'Locked after failed attempts',
  revoked: 'Revoked (waiting for setup)',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

/** The link, shown once: only its hash is kept, so it cannot be shown again. */
function SetupLinkDialog({
  link,
  name,
  onClose,
}: {
  link: SetupLinkResponse;
  name: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/account-setup#token=${link.token}`;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Setup link for {name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning" variant="outlined">
            This link is shown only now. Give it to {name} privately: anyone holding it can set up
            this sign-in until it is used or expires ({formatDateTime(link.expiresAt)}).
          </Alert>
          <TextField
            label="Setup link"
            value={url}
            fullWidth
            multiline
            slotProps={{ htmlInput: { readOnly: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={() => {
            void navigator.clipboard.writeText(url).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The linked employee, named and linked only where the session may read employees. */
function LinkedEmployee({ employeeId }: { employeeId: string | null }) {
  const session = useSession();
  const canSee = hasCapability(session.data, 'hr.employee:view');
  const employee = useQuery({
    queryKey: ['hr', 'employee', employeeId],
    enabled: canSee && employeeId !== null,
    retry: false,
    queryFn: () => getData(`/api/v1/hr/employees/${employeeId ?? ''}`, EmployeeSchema),
  });
  if (employeeId === null) return <>Not linked</>;
  if (!canSee) return <>Linked (employee records not visible to you)</>;
  if (employee.isPending) return <>…</>;
  if (employee.data === undefined) return <>Linked (not visible)</>;
  return (
    <PersonName name={employee.data.name} employeeId={employeeId}>
      <RecordLink to={`/hr/employees/${employeeId}`}>
        {employee.data.code} — {employee.data.name}
      </RecordLink>
    </PersonName>
  );
}

/**
 * The User ↔ Employee link (SDD §11.3; FRD-USR-002, FRD-HRP-011). It says which employee record
 * this user is, and nothing else: it creates no access, and only grants marked "own records only"
 * reach that record. The employee is searched on the server among active employees; one already
 * linked to another user is refused by the API.
 */
function EmployeeLinkSection({ user }: { user: UserSummaryDto }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canViewEmployees = hasCapability(session.data, 'hr.employee:view');
  const [chosen, setChosen] = useState<EmployeeDto | null>(null);
  const save = useMutation({
    mutationFn: (employeeId: string | null) =>
      patchJson(
        `${USERS}/${user.id}`,
        { employeeId },
        UserSummarySchema,
        session.data?.csrfToken ?? '',
        user.version,
      ),
    onSuccess: async () => {
      setChosen(null);
      await queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });
    },
  });
  return (
    <Section title="Employee link">
      <Stack spacing={1.5} sx={{ p: 2 }}>
        {!canViewEmployees ? (
          <Alert severity="info" variant="outlined">
            Choosing an employee needs permission to view employees.
          </Alert>
        ) : (
          <>
            <Box sx={{ maxWidth: 480 }}>
              <EmployeePicker value={chosen} onChange={setChosen} />
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={save.isPending || chosen === null || chosen.id === user.employeeId}
                onClick={() => {
                  if (chosen !== null) save.mutate(chosen.id);
                }}
              >
                {user.employeeId === null ? 'Link employee' : 'Change linked employee'}
              </Button>
              {user.employeeId !== null && (
                <Button
                  size="small"
                  color="warning"
                  disabled={save.isPending}
                  onClick={() => {
                    save.mutate(null);
                  }}
                >
                  Remove link
                </Button>
              )}
            </Stack>
          </>
        )}
        <Typography variant="caption" color="text.secondary">
          The link grants no access. Only permissions marked &ldquo;own records only&rdquo; reach
          the linked record.
        </Typography>
        {save.isError && (
          <Alert severity="error" variant="outlined">
            {save.error.message}
          </Alert>
        )}
      </Stack>
    </Section>
  );
}

/** Current is the API's answer, on the database date; the others have ended or not started. */
function AssignmentState({ assignment }: { assignment: RoleAssignmentSummaryDto }) {
  const session = useSession();
  if (assignment.current)
    return <Chip size="small" color="success" variant="outlined" label="Current" />;
  const today = businessToday(session.data?.businessTimezone ?? 'Asia/Dubai');
  const ended = assignment.effectiveTo !== null && assignment.effectiveTo < today;
  return (
    <Chip
      size="small"
      variant="outlined"
      color={ended ? 'default' : 'info'}
      label={ended ? 'Ended' : 'Scheduled'}
    />
  );
}

/** The assignment history, past, current and future (ERD §8.1), each endable with permission. */
function Assignments({ user }: { user: UserSummaryDto }) {
  const session = useSession();
  const canEnd = hasCapability(session.data, 'platform.role_assignment:change');
  const canSeeRoles = hasCapability(session.data, 'platform.role:view');
  const scope = useScopeNames();
  const assignments = useAssignments(user.id);
  const [ending, setEnding] = useState<RoleAssignmentSummaryDto | null>(null);
  const today = businessToday(session.data?.businessTimezone ?? 'Asia/Dubai');
  return (
    <Section title="Role assignments">
      {assignments.isPending && <LoadingState label="Loading role assignments" />}
      {assignments.isError && <ErrorState message={assignments.error.message} />}
      {assignments.data?.length === 0 && (
        <EmptyState
          title="No role assigned"
          detail="The user has no access until a role is assigned."
        />
      )}
      {assignments.data !== undefined && assignments.data.length > 0 && (
        <Table size="small" aria-label="Role assignments">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>State</TableCell>
              {canEnd && <TableCell align="right">Action</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.data.map((a) => {
              const ended = a.effectiveTo !== null && a.effectiveTo < today && !a.current;
              return (
                <TableRow key={a.id}>
                  <TableCell>
                    {canSeeRoles ? (
                      <RecordLink to={`/admin/roles/${encodeURIComponent(a.roleCode)}`}>
                        {a.roleName}
                      </RecordLink>
                    ) : (
                      a.roleName
                    )}
                    {a.isPrivileged && (
                      <Typography variant="caption" color="primary" component="div">
                        Privileged
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <ScopeText scope={a} names={scope} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{a.effectiveFrom}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {a.effectiveTo ?? 'Open-ended'}
                  </TableCell>
                  <TableCell>
                    <AssignmentState assignment={a} />
                  </TableCell>
                  {canEnd && (
                    <TableCell align="right">
                      {!ended && (
                        <Button
                          size="small"
                          color="warning"
                          aria-label={`End ${a.roleName}`}
                          onClick={(event) => {
                            event.currentTarget.focus();
                            setEnding(a);
                          }}
                        >
                          End
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {ending !== null && (
        <EndAssignmentDialog
          userId={user.id}
          assignment={ending}
          onClose={() => {
            setEnding(null);
          }}
        />
      )}
    </Section>
  );
}

/**
 * Ends an assignment: at once, or after a chosen last day. It is end-dated, never deleted, so the
 * history stays (ERD §8.1). The API refuses an end that would leave nobody able to administer
 * access, and says so.
 */
function EndAssignmentDialog({
  userId,
  assignment,
  onClose,
}: {
  userId: string;
  assignment: RoleAssignmentSummaryDto;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'now' | 'date'>('now');
  const [lastDay, setLastDay] = useState('');
  const end = useMutation({
    mutationFn: () =>
      postJson(
        `/api/v1/platform/role-assignments/${assignment.id}/end`,
        mode === 'date' ? { effectiveTo: lastDay } : {},
        RoleAssignmentSummarySchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: assignment.version },
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: assignmentsKey(userId) });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
    },
  });
  return (
    <FormDialog
      open
      title={`End ${assignment.roleName}`}
      submitLabel="End assignment"
      canSubmit={mode === 'now' || lastDay !== ''}
      submitting={end.isPending}
      error={end.isError ? end.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        end.mutate();
      }}
    >
      <RadioGroup
        value={mode}
        onChange={(e) => {
          setMode(e.target.value === 'date' ? 'date' : 'now');
        }}
      >
        <FormControlLabel value="now" control={<Radio />} label="End now — access stops at once" />
        <FormControlLabel value="date" control={<Radio />} label="End after a last day" />
      </RadioGroup>
      {mode === 'date' && (
        <TextField
          label="Last effective day"
          type="date"
          required
          value={lastDay}
          onChange={(e) => {
            setLastDay(e.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText="The assignment is effective up to and including this day."
        />
      )}
      <Typography variant="body2" color="text.secondary">
        The assignment is kept in the history with its end date. The change is audited.
      </Typography>
    </FormDialog>
  );
}

/**
 * A new assignment of an active role, with its dates and optional organizational scope
 * (FRD-USR-007): a scoped assignment reaches only records of that department, cost centre, project
 * or location in the modules that scope their records. The project is searched on the server and
 * offered only to an administrator who may view projects.
 */
function AssignRole({ user }: { user: UserSummaryDto }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canListRoles = hasCapability(session.data, 'platform.role:view');
  const scope = useScopeNames();
  const [role, setRole] = useState<RoleSummaryDto | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState(() =>
    businessToday(session.data?.businessTimezone),
  );
  const [effectiveTo, setEffectiveTo] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [project, setProject] = useState<ProjectDto | null>(null);
  const [client, setClient] = useState<ClientDto | null>(null);

  const roles = useQuery({
    queryKey: ['platform', 'roles'],
    enabled: canListRoles,
    queryFn: () => getData('/api/v1/platform/roles', z.array(RoleSummarySchema)),
  });
  const assign = useMutation({
    mutationFn: () =>
      postJson(
        `${USERS}/${user.id}/role-assignments`,
        {
          roleCode: role?.code ?? '',
          effectiveFrom,
          ...(effectiveTo !== '' ? { effectiveTo } : {}),
          ...(departmentId !== '' ? { departmentId } : {}),
          ...(costCenterId !== '' ? { costCenterId } : {}),
          ...(locationId !== '' ? { locationId } : {}),
          ...(project !== null ? { projectId: project.id } : {}),
          ...(client !== null ? { clientId: client.id } : {}),
        },
        z.object({ id: z.string() }),
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      setRole(null);
      setEffectiveTo('');
      setDepartmentId('');
      setCostCenterId('');
      setLocationId('');
      setProject(null);
      setClient(null);
      await queryClient.invalidateQueries({ queryKey: assignmentsKey(user.id) });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
    },
  });
  const scoped =
    departmentId !== '' ||
    costCenterId !== '' ||
    locationId !== '' ||
    project !== null ||
    client !== null;
  const select = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    rows: readonly { id: string; code: string; name: string; status: string }[],
    allowed: boolean,
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      disabled={!allowed}
      helperText={allowed ? undefined : `Needs permission to view ${label.toLowerCase()}s`}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      sx={{ minWidth: 200 }}
    >
      <MenuItem value="">Any</MenuItem>
      {rows
        .filter((row) => row.status === 'active')
        .map((row) => (
          <MenuItem key={row.id} value={row.id}>
            {row.code} — {row.name}
          </MenuItem>
        ))}
    </TextField>
  );

  return (
    <Section title="Assign a role">
      <Stack
        spacing={2}
        sx={{ p: 2.5 }}
        component="form"
        aria-label="Assign a role"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          assign.mutate();
        }}
      >
        {assign.isError && (
          <Alert severity="error" variant="outlined">
            {assign.error.message}
          </Alert>
        )}
        {assign.isSuccess && (
          <Alert severity="success" variant="outlined">
            Role assigned.
          </Alert>
        )}
        {!canListRoles && (
          <Alert severity="info" variant="outlined">
            Choosing a role needs permission to view roles.
          </Alert>
        )}
        <Autocomplete
          options={(roles.data ?? []).filter((r) => r.status === 'active')}
          value={role}
          onChange={(_, value) => {
            setRole(value);
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(a, b) => a.code === b.code}
          loading={roles.isPending && canListRoles}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.isPrivileged ? 'Privileged · ' : ''}
                    {String(option.grantCount)} permissions
                  </Typography>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => <TextField {...params} label="Role" required />}
        />
        {role?.isPrivileged === true && (
          <Alert severity="warning" variant="outlined">
            {role.name} is a privileged administration role. Every use is audited.
          </Alert>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Effective from"
            type="date"
            required
            fullWidth
            value={effectiveFrom}
            onChange={(e) => {
              setEffectiveFrom(e.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Effective to"
            type="date"
            fullWidth
            value={effectiveTo}
            onChange={(e) => {
              setEffectiveTo(e.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Optional: the last effective day"
          />
        </Stack>
        <Typography variant="subtitle2">Scope (optional)</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {select(
            'Department',
            departmentId,
            setDepartmentId,
            scope.departments,
            scope.can.departments,
          )}
          {select(
            'Cost centre',
            costCenterId,
            setCostCenterId,
            scope.costCentres,
            scope.can.costCentres,
          )}
          {select('Location', locationId, setLocationId, scope.locations, scope.can.locations)}
          {scope.can.projects && (
            <Box sx={{ minWidth: 240 }}>
              <ActiveProjectPicker label="Project" value={project} onChange={setProject} />
            </Box>
          )}
          {scope.can.clients && (
            <Box sx={{ minWidth: 240 }}>
              <ActiveClientPicker label="Client" value={client} onChange={setClient} />
            </Box>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {scoped
            ? 'A scoped role reaches only records of that department, cost centre, project, location or client, in the modules that scope their records; elsewhere it grants nothing.'
            : 'Without a scope the role applies to every record.'}
        </Typography>
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={assign.isPending || role === null || effectiveFrom === ''}
          >
            {assign.isPending ? 'Assigning…' : 'Assign role'}
          </Button>
        </Box>
      </Stack>
    </Section>
  );
}

function RenameDialog({ user, onClose }: { user: UserSummaryDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState(user.displayName);
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${USERS}/${user.id}`,
        { displayName: name.trim() },
        UserSummarySchema,
        session.data?.csrfToken ?? '',
        user.version,
      ),
    onSuccess: async () => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });
    },
  });
  return (
    <FormDialog
      open
      title="Edit name"
      submitLabel="Save"
      canSubmit={name.trim() !== '' && name.trim() !== user.displayName}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <TextField
        label="Display name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <Typography variant="caption" color="text.secondary">
        The email ({user.email}) is how the person signs in and is not changed here.
      </Typography>
    </FormDialog>
  );
}
